import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import {
  producaoService,
  RELATORIO_SABORES_PAGE_SIZE,
} from "../../../services/producaoService";
import { getErrorMessage } from "../../../utils/errorHandler";
import { useRelatorioSabores } from "../context/RelatorioSaboresContext";
import RelatorioQueryErrorAlert from "./RelatorioQueryErrorAlert";
import RelatorioSaboresTablePagination from "./RelatorioSaboresTablePagination";

interface RelatorioSaboresTableBlockProps {
  title?: string;
}

function formatQuantidadeInt(value: number): string {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

const RelatorioSaboresTableBlock: React.FC<RelatorioSaboresTableBlockProps> = ({
  title = "Tabela detalhada",
}) => {
  const { appliedFilters } = useRelatorioSabores();
  const [page, setPage] = useState(1);

  const filtersKey = useMemo(
    () => (appliedFilters ? JSON.stringify(appliedFilters) : ""),
    [appliedFilters]
  );

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: [
      "relatorio-sabores-detalhado-paginado",
      filtersKey,
      page,
      RELATORIO_SABORES_PAGE_SIZE,
    ],
    queryFn: () =>
      producaoService.getRelatorioSaboresDetalhadoPaginado(
        appliedFilters!,
        page,
        RELATORIO_SABORES_PAGE_SIZE
      ),
    enabled: !!appliedFilters?.dataInicio && !!appliedFilters?.dataFim,
    placeholderData: (prev) => prev,
  });

  const linhas = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalRegistros = data?.total ?? 0;
  const queryErrorMessage = isError ? getErrorMessage(error) : null;

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-6">
      <h3 className="mb-1 text-base font-semibold text-neutral-900">{title}</h3>
      <p className="mb-4 text-xs text-neutral-500">
        {RELATORIO_SABORES_PAGE_SIZE} linhas por página; totais atualizados
        abaixo.
      </p>
      {queryErrorMessage ? (
        <RelatorioQueryErrorAlert message={queryErrorMessage} />
      ) : (
      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="min-w-full divide-y divide-neutral-200 text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-neutral-700">
                Loja
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-700">
                Descrição
              </th>
              <th className="px-4 py-3 text-right font-semibold text-neutral-700">
                Quantidade
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 bg-white">
            {appliedFilters == null && (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-8 text-center text-neutral-500"
                >
                  Clique em{" "}
                  <span className="font-medium text-neutral-700">
                    Aplicar filtros
                  </span>{" "}
                  para carregar a tabela.
                </td>
              </tr>
            )}
            {appliedFilters != null && isFetching && data === undefined && (
              <tr>
                <td colSpan={3} className="px-4 py-8">
                  <div className="flex items-center justify-center gap-2 text-neutral-600">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    Carregando…
                  </div>
                </td>
              </tr>
            )}
            {appliedFilters != null && data != null && linhas.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-8 text-center text-neutral-500"
                >
                  Nenhum resultado no período. Ajuste os filtros e aplique
                  novamente.
                </td>
              </tr>
            )}
            {appliedFilters != null &&
              data != null &&
              linhas.map((row, idx) => (
                <tr
                  key={`${row.lojaId}-${row.saborId}-${row.tamanhoId}-${row.partes}-${idx}`}
                >
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-800">
                    {row.lojaNome}
                  </td>
                  <td className="px-4 py-3 text-neutral-800">{row.rotulo}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums text-neutral-900">
                    {formatQuantidadeInt(row.quantidade)}
                  </td>
                </tr>
              ))}
          </tbody>
          {appliedFilters != null && data != null && (
            <tfoot className="border-t-2 border-neutral-200 bg-neutral-50">
              <tr>
                <td
                  colSpan={2}
                  className="px-4 py-3 text-sm font-semibold text-neutral-800"
                >
                  Totais
                </td>
                <td className="px-4 py-3 text-right text-sm text-neutral-700">
                  <div className="tabular-nums">
                    <div>
                      Geral:{" "}
                      <span className="font-bold text-neutral-900">
                        {formatQuantidadeInt(data.totalQuantidadeGeral)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs font-normal">
                      Nesta página:{" "}
                      <span className="font-semibold text-neutral-900">
                        {formatQuantidadeInt(data.totalQuantidadePagina)}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
      {!queryErrorMessage && appliedFilters != null && data != null && (
        <RelatorioSaboresTablePagination
          page={data.page}
          totalPages={totalPages}
          totalRegistros={totalRegistros}
          totalGeralFmt={formatQuantidadeInt(data.totalQuantidadeGeral)}
          totalPaginaFmt={formatQuantidadeInt(data.totalQuantidadePagina)}
          isLoading={isFetching}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
};

export default RelatorioSaboresTableBlock;
